import dayjs from 'dayjs'
import { FilterState } from '../types'

// 截断历史记录中的人名
export const truncateHistoryPersonName = (name: string) => {
  const chars = Array.from(name || '')
  if (chars.length <= 5) return name
  return `${chars.slice(0, 2).join('')}...${chars.slice(-2).join('')}`
}

export const buildWebSearchHistoryId = (filter: FilterState) => {
  const userIDs = (filter.departmentUsers || [])
    .map((u: any) => u?.userID || u?.id)
    .filter(Boolean)
    .sort()

  const deptIDs = (filter.departments || [])
    .map((d: any) => d?.id)
    .filter(Boolean)
    .sort()

  const startMs = filter.times?.[0]?.valueOf?.() || 0
  const endMs = filter.times?.[1]?.valueOf?.() || 0

  return `u:${userIDs.join(',')}|d:${deptIDs.join(',')}|t:${startMs}-${endMs}`
}

const buildQuickTimeText = (filter: FilterState) => {
  if (!filter.times || filter.times.length !== 2) return null
  const [start, end] = filter.times

  const presets: Array<{ label: string; start: any; end: any }> = [
    {
      label: '今天',
      start: dayjs().startOf('day'),
      end: dayjs().endOf('day')
    },
    {
      label: '昨天',
      start: dayjs().add(-1, 'day').startOf('day'),
      end: dayjs().add(-1, 'day').endOf('day')
    },
    {
      label: '本周',
      start: dayjs().startOf('weeks'),
      end: dayjs().endOf('days')
    },
    {
      label: '上周',
      start: dayjs().add(-1, 'weeks').startOf('weeks'),
      end: dayjs().add(-1, 'weeks').endOf('weeks')
    },
    {
      label: '本月',
      start: dayjs().startOf('month'),
      end: dayjs().endOf('days')
    },
    {
      label: '上月',
      start: dayjs().add(-1, 'month').startOf('month'),
      end: dayjs().add(-1, 'month').endOf('month')
    },
    {
      label: '今年',
      start: dayjs().startOf('year'),
      end: dayjs().endOf('days')
    },
    {
      label: '去年',
      start: dayjs().add(-1, 'year').startOf('year'),
      end: dayjs().add(-1, 'year').endOf('year')
    },
    {
      label: '过去7天',
      start: dayjs().add(-7, 'days').startOf('days'),
      end: dayjs().add(-1, 'days').endOf('days')
    },
    {
      label: '过去30天',
      start: dayjs().add(-30, 'days').startOf('days'),
      end: dayjs().add(-1, 'days').endOf('days')
    },
    {
      label: '过去2个月',
      start: dayjs().add(-2, 'months').startOf('days'),
      end: dayjs().add(-1, 'days').endOf('days')
    },
    {
      label: '过去3个月',
      start: dayjs().add(-3, 'months').startOf('days'),
      end: dayjs().add(-1, 'days').endOf('days')
    },
    {
      label: '过去6个月',
      start: dayjs().subtract(6, 'months').startOf('day'),
      end: dayjs().endOf('day')
    },
    {
      label: '过去一年',
      start: dayjs().add(-1, 'years').startOf('days'),
      end: dayjs().add(-1, 'days').endOf('days')
    }
  ]

  const match = presets.find(
    (p) => start.isSame(p.start, 'day') && end.isSame(p.end, 'day')
  )
  return match?.label || null
}

const buildCustomTimeText = (filter: FilterState) => {
  if (!filter.times || filter.times.length !== 2) return ''
  const [start, end] = filter.times

  if (start.isSame(end, 'day')) {
    return start.format('M月D日')
  }

  if (start.year() === end.year()) {
    return `${start.format('M月D日')}-${end.format('M月D日')}`
  }

  return `${start.format('YYYY年M月D日')}-${end.format('YYYY年M月D日')}`
}

const buildPeopleText = (filter: FilterState) => {
  const totalPickedMembers =
    filter.departmentUsers.length +
    (filter.departments || []).reduce((acc, dept) => acc + (dept?.num || 0), 0)

  const personNames = (filter.departmentUsers || [])
    .map((u: any) => u?.name)
    .filter(Boolean) as string[]

  const deptNames = (filter.departments || [])
    .map((d: any) => d?.name)
    .filter(Boolean) as string[]

  if (personNames.length > 0) {
    if (personNames.length === 1) {
      return truncateHistoryPersonName(personNames[0])
    }
    if (personNames.length === 2) {
      return `${truncateHistoryPersonName(
        personNames[0]
      )}、${truncateHistoryPersonName(personNames[1])}`
    }
    return `${truncateHistoryPersonName(
      personNames[0]
    )}、${truncateHistoryPersonName(personNames[1])}等${totalPickedMembers}人`
  }

  // “部门名/等x人”
  if (deptNames.length === 1) {
    return truncateHistoryPersonName(deptNames[0])
  }
  if (deptNames.length === 2) {
    return `${truncateHistoryPersonName(
      deptNames[0]
    )}、${truncateHistoryPersonName(deptNames[1])}`
  }

  return `${truncateHistoryPersonName(
    deptNames[0]
  )}、${truncateHistoryPersonName(deptNames[1])}等${totalPickedMembers}人`
}

export const buildWebSearchHistoryText = (filter: FilterState) => {
  const peopleText = buildPeopleText(filter)
  const timeText = buildQuickTimeText(filter) || buildCustomTimeText(filter)
  return `${peopleText}${timeText}拍的照片`
}

export const buildWebSearchKeywordFilterHistoryId = (filter: FilterState) => {
  const keywordsKey = (filter.searchKeywords || []).slice().sort().join(',')

  return `k:${keywordsKey}|${buildWebSearchHistoryId(filter)}`
}

export const buildWebSearchKeywordFilterHistoryText = (filter: FilterState) => {
  const totalPickedMembers =
    filter.departmentUsers.length +
    (filter.departments || []).reduce((acc, dept) => acc + (dept?.num || 0), 0)

  const personNames = (filter.departmentUsers || [])
    .map((u: any) => u?.name)
    .filter(Boolean) as string[]

  const deptNames = (filter.departments || [])
    .map((d: any) => d?.name)
    .filter(Boolean) as string[]

  const getPeopleText = () => {
    if (personNames.length === 0 && deptNames.length === 0) {
      return ''
    }

    if (personNames.length > 0) {
      if (personNames.length === 1) {
        return truncateHistoryPersonName(personNames[0])
      }
      if (personNames.length === 2) {
        return `${truncateHistoryPersonName(
          personNames[0]
        )}、${truncateHistoryPersonName(personNames[1])}`
      }
      return `${truncateHistoryPersonName(
        personNames[0]
      )}、${truncateHistoryPersonName(personNames[1])}等${totalPickedMembers}人`
    }

    if (deptNames.length === 1) {
      return truncateHistoryPersonName(deptNames[0])
    }
    if (deptNames.length === 2) {
      return `${truncateHistoryPersonName(
        deptNames[0]
      )}、${truncateHistoryPersonName(deptNames[1])}`
    }

    return `${truncateHistoryPersonName(
      deptNames[0]
    )}、${truncateHistoryPersonName(deptNames[1])}等${totalPickedMembers}人`
  }

  const peopleText = getPeopleText()
  const timeText = buildQuickTimeText(filter) || buildCustomTimeText(filter)
  const keywordText = (filter.searchKeywords || []).length
    ? filter.searchKeywords.join('、')
    : ''

  return `${peopleText}${timeText}拍的${keywordText}`
}
